import * as React from 'react'
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    ListSubheader,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'

const PLUGIN_KEY = 'skips_trade'
const SETTINGS_KEY = 'skipsTradeConfig'
const DEFAULT_CONFIG = Object.freeze({
    trades: []
})

const SKIP_META = Object.freeze({
    MS1: { label: '1min' },
    MS2: { label: '5min' },
    MS3: { label: '10min' },
    MS4: { label: '30min' },
    MS5: { label: '1h' },
    MS6: { label: '5h' },
    MS7: { label: '24h' }
})

const BASE_UP_TRADE_PAIRS = Object.freeze([
    ['MS1', 'MS3', '11:1'],
    ['MS1', 'MS4', '33:1'],
    ['MS1', 'MS5', '66:1'],
    ['MS2', 'MS4', '7:1'],
    ['MS2', 'MS5', '13:1'],
    ['MS2', 'MS6', '66:1'],
    ['MS3', 'MS5', '7:1'],
    ['MS3', 'MS6', '33:1'],
    ['MS4', 'MS6', '11:1'],
    ['MS4', 'MS7', '49:1'],
    ['MS5', 'MS6', '5:1'],
    ['MS5', 'MS7', '25:1'],
    ['MS6', 'MS7', '5:1']
])

const createTradeDefinition = (sourceCode, targetCode, ratioLabel, direction) => ({
    id: `${direction}:${sourceCode}:${targetCode}`,
    direction,
    sourceCode,
    targetCode,
    ratioLabel,
    label: `${SKIP_META[sourceCode]?.label ?? sourceCode} -> ${SKIP_META[targetCode]?.label ?? targetCode}`
})

const TRADE_DEFINITIONS = Object.freeze([
    ...BASE_UP_TRADE_PAIRS.map(([sourceCode, targetCode, ratioLabel]) =>
        createTradeDefinition(sourceCode, targetCode, ratioLabel, 'up')),
    ...BASE_UP_TRADE_PAIRS.map(([sourceCode, targetCode, ratioLabel]) => {
        const downRatios = {
            '11:1': '1:9',
            '33:1': '1:29',
            '66:1': '1:59',
            '7:1': '1:5',
            '13:1': '1:11',
            '49:1': '1:47',
            '5:1': '1:4',
            '25:1': '1:23'
        }

        return createTradeDefinition(targetCode, sourceCode, downRatios[ratioLabel] ?? '1:4', 'down')
    })
])

const TRADE_DEFINITION_MAP = new Map(TRADE_DEFINITIONS.map(definition => [definition.id, definition]))

const PRIMARY_CARD_SX = {
    backgroundColor: 'rgba(18, 29, 48, 0.82)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '22px',
    boxShadow: '0 16px 34px rgba(0,0,0,0.18)'
}

const NESTED_CARD_SX = {
    borderRadius: '18px',
    border: '1px solid rgba(129, 146, 178, 0.14)',
    backgroundColor: 'rgba(20, 31, 52, 0.5)'
}

const FIELD_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        minHeight: 44,
        borderRadius: '10px',
        backgroundColor: 'rgba(23, 34, 55, 0.72)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

const SELECT_SX = {
    minWidth: 250,
    borderRadius: '10px',
    backgroundColor: 'rgba(24, 36, 57, 0.8)',
    '& .MuiSelect-select': {
        py: 1.35,
        fontSize: '1rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

const TOGGLE_SWITCH_SX = {
    width: 48,
    height: 29,
    p: 0,
    '& .MuiSwitch-switchBase': {
        p: '2.5px',
        '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#ffffff'
        },
        '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#2f7cff',
            opacity: 1
        }
    },
    '& .MuiSwitch-thumb': {
        width: 24,
        height: 24,
        boxShadow: 'none'
    },
    '& .MuiSwitch-track': {
        opacity: 1,
        borderRadius: '999px',
        backgroundColor: 'rgba(111, 123, 143, 0.9)'
    }
}

const OUTLINED_ACTION_SX = {
    minHeight: 44,
    px: 1.55,
    borderRadius: '10px',
    borderColor: 'rgba(112, 136, 174, 0.34)',
    backgroundColor: 'rgba(42, 54, 78, 0.56)',
    color: '#eef4ff',
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': {
        borderColor: 'rgba(112, 136, 174, 0.5)',
        backgroundColor: 'rgba(50, 63, 88, 0.7)'
    }
}

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const createTradeId = () =>
    `trade-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const normalizeDigits = value =>
    String(value ?? '').replace(/[^\d]/g, '')

const normalizeTradeEntry = value => {
    const tradeTypeId = String(value?.tradeTypeId ?? '').trim()
    if (!TRADE_DEFINITION_MAP.has(tradeTypeId))
        return null

    const noUpperLimit =
        typeof value?.noUpperLimit === 'boolean' ?
            value.noUpperLimit :
            [undefined, null, ''].includes(value?.maxThreshold)

    return {
        id: String(value?.id ?? createTradeId()),
        tradeTypeId,
        enabled: value?.enabled !== false,
        minThreshold: normalizeDigits(value?.minThreshold),
        maxThreshold: noUpperLimit ? '' : normalizeDigits(value?.maxThreshold),
        noUpperLimit,
        keepAmount: normalizeDigits(value?.keepAmount)
    }
}

const normalizeSkipsTradeConfig = value => {
    const source = value && typeof value === 'object' ? value : DEFAULT_CONFIG
    const trades = Array.isArray(source.trades) ? source.trades : []

    return {
        trades: trades
            .map(normalizeTradeEntry)
            .filter(Boolean)
    }
}

const getDirectionColor = direction =>
    direction === 'up' ? '#19d98a' : '#ff9800'

const formatTradeRatioText = definition => {
    const [leftRatioText, rightRatioText] = String(definition?.ratioLabel ?? '1:1').split(':')
    const leftRatio = Number.parseInt(leftRatioText, 10)
    const rightRatio = Number.parseInt(rightRatioText, 10)
    const safeLeftRatio = Number.isFinite(leftRatio) && leftRatio > 0 ? leftRatio : 1
    const safeRightRatio = Number.isFinite(rightRatio) && rightRatio > 0 ? rightRatio : 1
    const sourceLabel = SKIP_META[definition?.sourceCode]?.label ?? definition?.sourceCode ?? 'source'
    const targetLabel = SKIP_META[definition?.targetCode]?.label ?? definition?.targetCode ?? 'target'

    if (definition?.direction === 'down')
        return `Converts ${safeLeftRatio} ${sourceLabel} skip${safeLeftRatio === 1 ? '' : 's'} into ${safeRightRatio} ${targetLabel} skips`

    return `Costs ${safeLeftRatio} ${sourceLabel} skips to get ${safeRightRatio} ${targetLabel} skip${safeRightRatio === 1 ? '' : 's'}`
}

const formatThresholdLine = (trade, definition) => {
    const sourceLabel = SKIP_META[definition?.sourceCode]?.label ?? definition?.sourceCode ?? 'source'
    const targetLabel = SKIP_META[definition?.targetCode]?.label ?? definition?.targetCode ?? 'target'
    const lines = [
        `${sourceLabel} skips > ${trade.minThreshold || '0'} (Min)`,
        `Keeps at least ${trade.keepAmount || '0'} ${sourceLabel} skips`
    ]

    if (!trade.noUpperLimit)
        lines.push(`${targetLabel} stays below ${trade.maxThreshold || '0'} (${targetLabel})`)

    return lines
}

const countTradeStats = trades => {
    const entries = Array.isArray(trades) ? trades : []
    const enabledCount = entries.filter(entry => entry.enabled).length
    const upTrades = entries.filter(entry => TRADE_DEFINITION_MAP.get(entry.tradeTypeId)?.direction === 'up').length
    const downTrades = entries.filter(entry => TRADE_DEFINITION_MAP.get(entry.tradeTypeId)?.direction === 'down').length

    return {
        total: entries.length,
        enabled: enabledCount,
        upTrades,
        downTrades
    }
}

function HelperText({ children }) {
    return (
        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 0.75 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.89rem', color: '#95a5c4', lineHeight: 1.35 }}>
                {children}
            </Typography>
        </Stack>
    )
}

function SectionHeading({ title, children, action }) {
    return (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: children ? 1.5 : 0 }}
        >
            <Box>
                <Typography sx={{ color: '#f5f7ff', fontSize: '1.55rem', fontWeight: 800 }}>
                    {title}
                </Typography>
                {children && (
                    <Typography sx={{ color: '#9badcb', fontSize: '1rem', mt: 0.4 }}>
                        {children}
                    </Typography>
                )}
            </Box>
            {action}
        </Stack>
    )
}

function StyledSwitch({ checked, onChange }) {
    return (
        <Switch
            checked={checked}
            onChange={event => onChange(event.target.checked)}
            sx={TOGGLE_SWITCH_SX}
        />
    )
}

function TradeCard({
    trade,
    definition,
    onToggle,
    onEdit,
    onDelete,
    onDragStart,
    onDragOver,
    onDrop
}) {
    const ratioColor = getDirectionColor(definition?.direction)

    return (
        <Paper
            draggable
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            sx={{
                p: 1.7,
                borderRadius: '16px',
                backgroundColor: 'rgba(18, 29, 48, 0.76)',
                border: '1px solid rgba(255,255,255,0.08)'
            }}
        >
            <Stack direction="row" spacing={1.15} alignItems="flex-start">
                <Box sx={{ pt: 0.2, color: '#6d7e9d', cursor: 'grab' }}>
                    <DragIndicatorRoundedIcon />
                </Box>

                <Box sx={{ flex: 1 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1.02rem', fontWeight: 800 }}>
                                {definition?.label ?? 'Unknown trade'}
                            </Typography>
                            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.7, flexWrap: 'wrap' }}>
                                <Chip
                                    label={definition?.direction === 'up' ? 'Up-Trade' : 'Down-Trade'}
                                    size="small"
                                    sx={{
                                        backgroundColor: `${ratioColor}20`,
                                        color: ratioColor,
                                        fontWeight: 800,
                                        border: `1px solid ${ratioColor}55`
                                    }}
                                />
                                <Typography sx={{ color: ratioColor, fontSize: '0.92rem', fontWeight: 800 }}>
                                    {definition?.ratioLabel ?? '1:1'}
                                </Typography>
                                <Typography sx={{ color: '#95a5c4', fontSize: '0.92rem' }}>
                                    {formatTradeRatioText(definition)}
                                </Typography>
                            </Stack>
                        </Box>

                        <Stack direction="row" spacing={0.3} alignItems="center">
                            <StyledSwitch checked={trade.enabled} onChange={onToggle} />
                            <IconButton onClick={onEdit} sx={{ color: '#c4d0e7' }}>
                                <EditRoundedIcon />
                            </IconButton>
                            <IconButton onClick={onDelete} sx={{ color: '#ff8a8a' }}>
                                <DeleteOutlineRoundedIcon />
                            </IconButton>
                        </Stack>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ mt: 1.45 }}>
                        <Chip
                            label={`Min ${trade.minThreshold || '0'}`}
                            sx={{ backgroundColor: 'rgba(64, 82, 113, 0.58)', color: '#eef4ff', fontWeight: 700 }}
                        />
                        <Chip
                            label={trade.noUpperLimit ? 'No upper limit' : `Max ${trade.maxThreshold || '0'}`}
                            sx={{ backgroundColor: 'rgba(64, 82, 113, 0.58)', color: '#eef4ff', fontWeight: 700 }}
                        />
                        <Chip
                            label={`Keep ${trade.keepAmount || '0'}`}
                            sx={{ backgroundColor: 'rgba(64, 82, 113, 0.58)', color: '#eef4ff', fontWeight: 700 }}
                        />
                    </Stack>
                </Box>
            </Stack>
        </Paper>
    )
}

function TradeEditorDialog({ open, trade, onClose, onSave }) {
    const [draft, setDraft] = React.useState(() =>
        normalizeTradeEntry(trade) ??
        {
            id: createTradeId(),
            tradeTypeId: TRADE_DEFINITIONS[0]?.id ?? '',
            enabled: true,
            minThreshold: '500',
            maxThreshold: '',
            noUpperLimit: true,
            keepAmount: '300'
        }
    )

    React.useEffect(() => {
        if (!open)
            return

        setDraft(
            normalizeTradeEntry(trade) ??
            {
                id: createTradeId(),
                tradeTypeId: TRADE_DEFINITIONS[0]?.id ?? '',
                enabled: true,
                minThreshold: '500',
                maxThreshold: '',
                noUpperLimit: true,
                keepAmount: '300'
            }
        )
    }, [open, trade])

    const selectedDefinition = TRADE_DEFINITION_MAP.get(draft.tradeTypeId) ?? TRADE_DEFINITIONS[0]
    const summaryLines = formatThresholdLine(draft, selectedDefinition)

    const handleSave = () => {
        const normalizedTrade = normalizeTradeEntry(draft)
        if (!normalizedTrade)
            return

        onSave(normalizedTrade)
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '22px',
                    backgroundColor: '#111a31',
                    border: '1px solid rgba(255,255,255,0.08)'
                }
            }}
        >
            <DialogTitle sx={{ px: 3, pt: 2.7, pb: 1.3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1.2} alignItems="center">
                        <AddRoundedIcon sx={{ color: '#f5f7ff' }} />
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.2rem', fontWeight: 800 }}>
                            Add New Trade
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} sx={{ color: '#8fa1bf' }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
                <Typography sx={{ color: '#9badcb', fontSize: '1rem', mt: 1.05 }}>
                    Configure when and how this trade should be executed.
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pb: 1.7 }}>
                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 1 }}>
                    Trade Type
                </Typography>
                <FormControl fullWidth sx={{ mb: 0.7 }}>
                    <Select
                        value={draft.tradeTypeId}
                        onChange={event => setDraft(current => ({ ...current, tradeTypeId: event.target.value }))}
                        sx={SELECT_SX}
                    >
                        <ListSubheader sx={{ color: '#19d98a', backgroundColor: '#111a31', fontWeight: 800 }}>
                            Up-Trades (Consolidate)
                        </ListSubheader>
                        {TRADE_DEFINITIONS
                            .filter(definition => definition.direction === 'up')
                            .map(definition => (
                                <MenuItem key={definition.id} value={definition.id}>
                                    {`${definition.label} (${definition.ratioLabel})`}
                                </MenuItem>
                            ))}
                        <ListSubheader sx={{ color: '#ff9800', backgroundColor: '#111a31', fontWeight: 800 }}>
                            Down-Trades (Split)
                        </ListSubheader>
                        {TRADE_DEFINITIONS
                            .filter(definition => definition.direction === 'down')
                            .map(definition => (
                                <MenuItem key={definition.id} value={definition.id}>
                                    {`${definition.label} (${definition.ratioLabel})`}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
                <HelperText>{formatTradeRatioText(selectedDefinition)}</HelperText>

                <Paper sx={{ ...NESTED_CARD_SX, p: 2, mt: 2.4 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                                Enabled
                            </Typography>
                            <Typography sx={{ color: '#9badcb', fontSize: '0.95rem', mt: 0.2 }}>
                                Trade will be executed when conditions are met
                            </Typography>
                        </Box>
                        <StyledSwitch
                            checked={draft.enabled}
                            onChange={checked => setDraft(current => ({ ...current, enabled: checked }))}
                        />
                    </Stack>
                </Paper>

                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mt: 2.8, mb: 1.2 }}>
                    Thresholds
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '0.98rem', fontWeight: 800, mb: 0.8 }}>
                            Min Threshold
                        </Typography>
                        <TextField
                            fullWidth
                            value={draft.minThreshold}
                            onChange={event => setDraft(current => ({ ...current, minThreshold: normalizeDigits(event.target.value) }))}
                            sx={FIELD_INPUT_SX}
                        />
                        <Typography sx={{ color: '#7385a6', fontSize: '0.86rem', mt: 0.65 }}>
                            Trade only if skips &gt; this value
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '0.98rem', fontWeight: 800, mb: 0.8 }}>
                            Max Threshold
                        </Typography>
                        <TextField
                            fullWidth
                            value={draft.maxThreshold}
                            disabled={draft.noUpperLimit}
                            onChange={event => setDraft(current => ({ ...current, maxThreshold: normalizeDigits(event.target.value) }))}
                            sx={FIELD_INPUT_SX}
                        />
                        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.7 }}>
                            <Checkbox
                                checked={draft.noUpperLimit}
                                onChange={event => setDraft(current => ({
                                    ...current,
                                    noUpperLimit: event.target.checked,
                                    maxThreshold: event.target.checked ? '' : current.maxThreshold
                                }))}
                                sx={{
                                    p: 0,
                                    color: '#86a7ff',
                                    '&.Mui-checked': {
                                        color: '#86a7ff'
                                    }
                                }}
                            />
                            <Typography sx={{ color: '#9badcb', fontSize: '0.92rem', fontWeight: 700 }}>
                                No upper limit
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '0.98rem', fontWeight: 800, mb: 0.8 }}>
                            Keep Amount
                        </Typography>
                        <TextField
                            fullWidth
                            value={draft.keepAmount}
                            onChange={event => setDraft(current => ({ ...current, keepAmount: normalizeDigits(event.target.value) }))}
                            sx={FIELD_INPUT_SX}
                        />
                        <Typography sx={{ color: '#7385a6', fontSize: '0.86rem', mt: 0.65 }}>
                            Keep at least this many {SKIP_META[selectedDefinition?.sourceCode]?.label ?? 'source'} skips after trading
                        </Typography>
                    </Grid>
                </Grid>

                <Paper
                    sx={{
                        ...NESTED_CARD_SX,
                        p: 2,
                        mt: 2.4,
                        backgroundColor: 'rgba(22, 34, 55, 0.88)'
                    }}
                >
                    <Typography sx={{ color: '#dfe9ff', fontSize: '0.98rem', fontWeight: 800 }}>
                        Trade will execute when:
                    </Typography>
                    <Stack component="ul" spacing={0.7} sx={{ mt: 1.05, mb: 0, pl: 2.2, color: '#c7d5ee' }}>
                        {summaryLines.map(line => (
                            <Typography key={line} component="li" sx={{ fontSize: '0.95rem', lineHeight: 1.45 }}>
                                {line}
                            </Typography>
                        ))}
                    </Stack>
                </Paper>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.7, pt: 0.5 }}>
                <Button onClick={onClose} variant="outlined" sx={OUTLINED_ACTION_SX}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" sx={{ ...OUTLINED_ACTION_SX, backgroundColor: 'rgba(88, 102, 126, 0.88)' }}>
                    Add Trade
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function ConfigurationDialog({ open, config, onClose, onSave }) {
    const [draftConfig, setDraftConfig] = React.useState(() => normalizeSkipsTradeConfig(config))
    const [editorOpen, setEditorOpen] = React.useState(false)
    const [editingTrade, setEditingTrade] = React.useState(null)
    const [dragIndex, setDragIndex] = React.useState(-1)

    React.useEffect(() => {
        if (!open)
            return

        setDraftConfig(normalizeSkipsTradeConfig(config))
        setEditorOpen(false)
        setEditingTrade(null)
        setDragIndex(-1)
    }, [config, open])

    const stats = React.useMemo(() => countTradeStats(draftConfig.trades), [draftConfig.trades])

    const handleTradeSave = nextTrade => {
        setDraftConfig(current => {
            const existingIndex = current.trades.findIndex(entry => entry.id === nextTrade.id)
            if (existingIndex === -1)
                return { ...current, trades: [...current.trades, nextTrade] }

            const nextTrades = [...current.trades]
            nextTrades[existingIndex] = nextTrade
            return { ...current, trades: nextTrades }
        })
        setEditorOpen(false)
        setEditingTrade(null)
    }

    const handleTradeDelete = tradeId => {
        setDraftConfig(current => ({
            ...current,
            trades: current.trades.filter(entry => entry.id !== tradeId)
        }))
    }

    const reorderTrades = (fromIndex, toIndex) => {
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex)
            return

        setDraftConfig(current => {
            const nextTrades = [...current.trades]
            const [movedTrade] = nextTrades.splice(fromIndex, 1)
            nextTrades.splice(toIndex, 0, movedTrade)
            return { ...current, trades: nextTrades }
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '22px',
                    backgroundColor: '#111a31',
                    border: '1px solid rgba(255,255,255,0.08)',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{ px: 3.2, pt: 2.8, pb: 1.2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1.2} alignItems="center">
                        <NorthEastRoundedIcon sx={{ color: '#19d98a' }} />
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.85rem', fontWeight: 800 }}>
                            Skips Trade Configuration
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} sx={{ color: '#8fa1bf' }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
                <Typography sx={{ color: '#9badcb', fontSize: '1rem', mt: 1.1 }}>
                    Configure automated skip trading. Trades are executed in order when the shop is available.
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ px: 3.2, pb: 1.8 }}>
                <SectionHeading
                    title={`Configured Trades (${stats.total})`}
                    action={
                        <Button
                            variant="contained"
                            startIcon={<AddRoundedIcon />}
                            onClick={() => {
                                setEditingTrade(null)
                                setEditorOpen(true)
                            }}
                            sx={{
                                minHeight: 42,
                                px: 1.8,
                                borderRadius: '12px',
                                backgroundColor: 'rgba(88, 102, 126, 0.88)',
                                color: '#f7fbff',
                                textTransform: 'none',
                                fontWeight: 800,
                                boxShadow: 'none'
                            }}
                        >
                            Add Trade
                        </Button>
                    }
                >
                    <Stack direction="row" spacing={1.6} alignItems="center" sx={{ mt: 0.35 }}>
                        <Typography sx={{ color: '#a8b7d3', fontSize: '0.98rem' }}>
                            {stats.enabled} enabled
                        </Typography>
                        <Typography sx={{ color: '#19d98a', fontSize: '0.98rem', fontWeight: 800 }}>
                            {stats.upTrades} up-trades
                        </Typography>
                        <Typography sx={{ color: '#ff9800', fontSize: '0.98rem', fontWeight: 800 }}>
                            {stats.downTrades} down-trades
                        </Typography>
                    </Stack>
                </SectionHeading>

                <Paper
                    sx={{
                        minHeight: 148,
                        p: draftConfig.trades.length > 0 ? 1.1 : 3.2,
                        borderRadius: '18px',
                        border: '1px dashed rgba(122, 141, 174, 0.28)',
                        backgroundColor: 'rgba(17, 29, 47, 0.55)'
                    }}
                >
                    {draftConfig.trades.length <= 0 ? (
                        <Stack alignItems="center" justifyContent="center" spacing={0.6} sx={{ minHeight: 112 }}>
                            <Typography sx={{ color: '#b8c6df', fontSize: '1.55rem' }}>
                                No trades configured
                            </Typography>
                            <Typography sx={{ color: '#8ea0c0', fontSize: '1rem' }}>
                                Click "Add Trade" to create your first skip trade
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack spacing={1.1}>
                            {draftConfig.trades.map((trade, index) => {
                                const definition = TRADE_DEFINITION_MAP.get(trade.tradeTypeId)

                                return (
                                    <TradeCard
                                        key={trade.id}
                                        trade={trade}
                                        definition={definition}
                                        onToggle={checked => setDraftConfig(current => ({
                                            ...current,
                                            trades: current.trades.map(entry =>
                                                entry.id === trade.id ? { ...entry, enabled: checked } : entry)
                                        }))}
                                        onEdit={() => {
                                            setEditingTrade(trade)
                                            setEditorOpen(true)
                                        }}
                                        onDelete={() => handleTradeDelete(trade.id)}
                                        onDragStart={() => setDragIndex(index)}
                                        onDragOver={event => event.preventDefault()}
                                        onDrop={() => {
                                            reorderTrades(dragIndex, index)
                                            setDragIndex(-1)
                                        }}
                                    />
                                )
                            })}
                        </Stack>
                    )}
                </Paper>

                <Paper sx={{ ...NESTED_CARD_SX, p: 2.1, mt: 2.2 }}>
                    <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <InfoOutlinedIcon sx={{ color: '#7fb4ff', mt: 0.05 }} />
                        <Box>
                            <Typography sx={{ color: '#dbe6fb', fontSize: '0.98rem', fontWeight: 800 }}>
                                Order matters:
                                <Typography component="span" sx={{ color: '#aebddb', fontSize: '0.98rem' }}>
                                    {' '}Trades are executed from top to bottom. Drag to reorder for cascade trading.
                                </Typography>
                            </Typography>
                            <Typography sx={{ color: '#dbe6fb', fontSize: '0.98rem', fontWeight: 800, mt: 1 }}>
                                Shop availability:
                                <Typography component="span" sx={{ color: '#aebddb', fontSize: '0.98rem' }}>
                                    {' '}The Skips Trade Shop appears periodically. The bot automatically detects when it is available.
                                </Typography>
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            </DialogContent>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

            <DialogActions sx={{ px: 3.2, py: 2.4 }}>
                <Button onClick={onClose} variant="outlined" sx={OUTLINED_ACTION_SX}>
                    Cancel
                </Button>
                <Button
                    onClick={() => onSave(normalizeSkipsTradeConfig(draftConfig))}
                    variant="contained"
                    sx={{ ...OUTLINED_ACTION_SX, backgroundColor: 'rgba(88, 102, 126, 0.88)' }}
                >
                    Save Configuration
                </Button>
            </DialogActions>

            <TradeEditorDialog
                open={editorOpen}
                trade={editingTrade}
                onClose={() => {
                    setEditorOpen(false)
                    setEditingTrade(null)
                }}
                onSave={handleTradeSave}
            />
        </Dialog>
    )
}

export default function SkipsTradeSettingsPanel({ userPlugins, onChange }) {
    const [dialogOpen, setDialogOpen] = React.useState(false)

    const config = React.useMemo(
        () => normalizeSkipsTradeConfig(userPlugins?.[PLUGIN_KEY]?.[SETTINGS_KEY] ?? DEFAULT_CONFIG),
        [userPlugins]
    )
    const stats = React.useMemo(() => countTradeStats(config.trades), [config.trades])

    const commit = React.useCallback(nextConfig => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[PLUGIN_KEY] ??= {}
        nextPlugins[PLUGIN_KEY][SETTINGS_KEY] = normalizeSkipsTradeConfig(nextConfig)
        onChange(nextPlugins)
    }, [onChange, userPlugins])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.4 }}>
            <Paper sx={PRIMARY_CARD_SX}>
                <Box sx={{ p: { xs: 2.2, md: 3.2 } }}>
                    <Stack direction="row" spacing={1.3} alignItems="center" sx={{ mb: 0.7 }}>
                        <SwapHorizRoundedIcon sx={{ color: '#f5f7ff', fontSize: 21 }} />
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.55rem', fontWeight: 800 }}>
                            Skips Trade Settings
                        </Typography>
                    </Stack>

                    <Typography sx={{ color: '#9badcb', fontSize: '1rem', mb: 4 }}>
                        Configure automated trading of time skips. Trade lower denomination skips for higher ones (up-trades) or split larger skips into smaller ones (down-trades). The shop appears periodically for about 1 hour.
                    </Typography>

                    <Paper
                        onClick={() => setDialogOpen(true)}
                        sx={{
                            ...NESTED_CARD_SX,
                            px: 2,
                            py: 1.4,
                            cursor: 'pointer',
                            transition: 'transform 0.18s ease, border-color 0.18s ease',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                borderColor: 'rgba(129, 146, 178, 0.22)'
                            }
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                            <Stack direction="row" spacing={1.1} alignItems="center">
                                <NorthEastRoundedIcon sx={{ color: '#19d98a' }} />
                                <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>
                                    Configure Trades
                                </Typography>
                            </Stack>
                            <Chip
                                label={`${stats.total} trades (${stats.enabled} enabled)`}
                                sx={{
                                    backgroundColor: 'rgba(56, 73, 103, 0.78)',
                                    color: '#eef4ff',
                                    fontWeight: 800,
                                    border: '1px solid rgba(129,146,178,0.2)'
                                }}
                            />
                        </Stack>
                    </Paper>
                </Box>
            </Paper>

            <ConfigurationDialog
                open={dialogOpen}
                config={config}
                onClose={() => setDialogOpen(false)}
                onSave={nextConfig => {
                    commit(nextConfig)
                    setDialogOpen(false)
                }}
            />
        </Box>
    )
}
